import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { setupAxiosInterceptors } from "./utils/axiosConfig";
import Auth0FarelanesConfig from '../src/auth_config.json';

// Component to setup axios interceptors after Auth0 is initialized
// This must be inside Auth0Provider to access useAuth0 hook
const AxiosInterceptorSetup = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      // Setup axios interceptors with token function
      setupAxiosInterceptors(async (options = {}) => {
        try {
          return await getAccessTokenSilently({
            authorizationParams: {
              audience: options.audience || Auth0FarelanesConfig.audience,
            },
            cacheMode: options.cacheMode || 'on',
          });
        } catch (error) {
          // Only redirect if user is not authenticated AND we haven't already initiated a redirect
          // Don't redirect if user is authenticated - let the request fail gracefully
          if (!isAuthenticated && !redirectingRef.current) {
            if (error?.error === 'missing_refresh_token' || 
                error?.message?.includes('Missing Refresh Token') ||
                error?.error === 'login_required' ||
                error?.error === 'consent_required') {
              redirectingRef.current = true;
              console.log('Authentication required, redirecting to login...');
              loginWithRedirect({
                appState: {
                  returnTo: window.location.pathname + window.location.search,
                },
              });
            }
          }
          // Always reject the promise to prevent the request from proceeding
          return Promise.reject(error);
        }
      });
    }
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  // Reset redirect flag when authentication state changes
  useEffect(() => {
    redirectingRef.current = false;
  }, [isAuthenticated]);

  return <>{children}</>;
};

export const Auth0ProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();

  const domain = Auth0FarelanesConfig.domain;
  const clientId = Auth0FarelanesConfig.clientId;
  const redirectUri = Auth0FarelanesConfig.callback_url;
  const audience = Auth0FarelanesConfig.audience;

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  const onError = (error) => {
    console.error('Auth0 Error:', error);
    // Handle specific error types
    if (error?.error === 'login_required') {
      // User needs to login - handled by AuthenticationGuard
      return;
    }
    // Log other errors for debugging
    if (error?.error !== 'login_required') {
      console.error('Unexpected Auth0 error:', error);
    }
  };

  if (!(domain && clientId && redirectUri && audience)) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Configuration Error</h1>
        <p>Auth0 configuration is missing. Please check auth_config.json</p>
      </div>
    );
  }

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        audience: audience,
        redirect_uri: redirectUri,
        screen_hint: "signup",
      }}
      onRedirectCallback={onRedirectCallback}
      onError={onError}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      advancedOptions={{
        defaultScope: 'openid profile email offline_access'
      }}
    >
      <AxiosInterceptorSetup>
        {children}
      </AxiosInterceptorSetup>
    </Auth0Provider>
  );
};
